import { useMemo, useState, type FormEvent } from 'react';
import { formatManilaDate, formatInclusiveManilaPeriodEnd } from '@dispatchdash/shared';
import {
  payoutsApi,
  type Payout,
  type PayoutEntry,
  type PayoutRunDetail,
  type PayCorrectionHistoryItem,
} from '@/api/client';
import { useAsync } from '@/hooks';
import { Layout } from '@/components/Layout';
import { DataTable } from '@/components/DataTable';
import { StatusBadge } from '@/components/StatusBadge';
import { Select } from '@/components/Select';
import { Input } from '@/components/Input';
import { Button } from '@/components/Button';
import { Modal } from '@/components/Modal';
import { useConfirm, useToast } from '@/components/Feedback';
import { formatDate, formatDateTime, formatPeso, humanize } from '@/lib/format';
import { adminNavItems } from '@/pages/navItems';

/** Current UTC fortnight: YYYY-MM-H1 (1–15) or YYYY-MM-H2 (16–EOM). */
function currentFortnightPeriod() {
  const now = new Date();
  const year = now.getUTCFullYear();
  const month = String(now.getUTCMonth() + 1).padStart(2, '0');
  const half = now.getUTCDate() <= 15 ? 'H1' : 'H2';
  return `${year}-${month}-${half}`;
}

function fortnightOptions(count = 8): Array<{ value: string; label: string }> {
  const options: Array<{ value: string; label: string }> = [];
  const now = new Date();
  let year = now.getUTCFullYear();
  let month = now.getUTCMonth(); // 0-based
  let half: 'H1' | 'H2' = now.getUTCDate() <= 15 ? 'H1' : 'H2';

  for (let i = 0; i < count; i++) {
    const mm = String(month + 1).padStart(2, '0');
    const value = `${year}-${mm}-${half}`;
    const label =
      half === 'H1'
        ? `${year}-${mm} · 1–15`
        : `${year}-${mm} · 16–end`;
    options.push({ value, label });
    if (half === 'H2') {
      half = 'H1';
    } else {
      half = 'H2';
      month -= 1;
      if (month < 0) {
        month = 11;
        year -= 1;
      }
    }
  }
  return options;
}

function runPeriodLabel(run: Payout): string {
  return (
    run.period ??
    run.periodLabel ??
    (run.periodStart && run.periodEnd
      ? `${formatManilaDate(run.periodStart)} → ${formatInclusiveManilaPeriodEnd(run.periodEnd, run.periodStart)}`
      : '—')
  );
}

function runTotalAmount(run: Payout): string {
  return run.totalAmount ?? run.total ?? '0';
}

function entryAmount(row: PayoutEntry): string {
  const base = Number(row.overrideAmount ?? row.baseAmount);
  const extra = Number(row.extraStopAmount ?? 0);
  return (base + extra).toFixed(2);
}

/** What this pay is worth now. An adjustment row carries only a difference and a paid
 * original may have later adjustments, so the latest correction of the entry's chain
 * (history is newest first) gives the current total. */
function currentEntryValue(row: PayoutEntry, corrections: PayCorrectionHistoryItem[] | undefined): string {
  const rootId = row.rootEntryId ?? row.id;
  const latest = corrections?.find((c) => c.rootEntryId === rootId);
  return latest?.to ?? entryAmount(row);
}

const ENTRY_KIND_LABELS: Record<string, string> = {
  correction: 'Corrected',
  adjustment: 'Adjustment from an earlier payout',
};

function CorrectionHistory({ items }: { items: PayCorrectionHistoryItem[] }) {
  return (
    <ul className="correction-history">
      {items.map((c) => (
        <li key={c.id}>
          <div className="row-between">
            <span>
              <strong>{c.personName ?? 'Unnamed person'}</strong> · {humanize(c.role)}
            </span>
            <span className="money-cell">
              {formatPeso(c.from)} → {formatPeso(c.to)}
            </span>
          </div>
          <div className="text-muted correction-meta">
            {c.reason} · {c.by ?? 'Unknown user'} · {formatDateTime(c.at)}
            {c.mode === 'carry_forward' && (
              <> · difference of {formatPeso(c.entryAmount)} paid in a later payout run</>
            )}
          </div>
        </li>
      ))}
    </ul>
  );
}

function Money({ value }: { value: string | number | null | undefined }) {
  return <span className="money-cell">{formatPeso(value)}</span>;
}

function peopleCount(detail: PayoutRunDetail): number {
  return new Set(detail.entries.map((e) => e.personId)).size;
}

function canFinalizeRun(run: Payout): boolean {
  if (run.finalizedAt) return false;
  if (typeof run.canFinalize === 'boolean') return run.canFinalize;
  const label = run.periodLabel ?? run.period ?? '';
  return /H[12]$/i.test(label);
}

export function AdminPayouts() {
  const confirm = useConfirm();
  const toast = useToast();
  const { data: payouts, loading, error: listLoadError, reload } = useAsync(() => payoutsApi.list(), []);
  const [showAdd, setShowAdd] = useState(false);
  const [period, setPeriod] = useState(currentFortnightPeriod());
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const periodChoices = useMemo(() => fortnightOptions(), []);

  const [detailId, setDetailId] = useState<string | null>(null);
  const [detail, setDetail] = useState<PayoutRunDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState('');
  const [finalizing, setFinalizing] = useState(false);
  const [markingPaid, setMarkingPaid] = useState(false);

  const [correctionEntry, setCorrectionEntry] = useState<PayoutEntry | null>(null);
  const [correctionAmount, setCorrectionAmount] = useState('');
  const [correctionReason, setCorrectionReason] = useState('');
  const [correctionSaving, setCorrectionSaving] = useState(false);
  const [correctionError, setCorrectionError] = useState('');

  const loadDetail = async (id: string) => {
    setDetailId(id);
    setDetailLoading(true);
    setDetailError('');
    try {
      setDetail(await payoutsApi.getDetail(id));
    } catch (err) {
      setDetailError((err as { message?: string }).message ?? 'Failed to load payout run detail');
    } finally {
      setDetailLoading(false);
    }
  };

  const closeDetail = () => {
    setDetailId(null);
    setDetail(null);
    setDetailError('');
  };

  const handleFinalize = async () => {
    if (!detailId || !detail) return;
    const ok = await confirm({
      title: 'Finalise payout run?',
      message: `Finalise payouts for ${runPeriodLabel(detail.run)}: ${peopleCount(detail)} people, ${detail.entries.length} entries, ${formatPeso(runTotalAmount(detail.run))}. Per-person statements are generated and can no longer be changed (later fixes need a correction).`,
      confirmLabel: 'Finalise payouts',
    });
    if (!ok) return;
    setFinalizing(true);
    setDetailError('');
    try {
      await payoutsApi.finalize(detailId);
      toast(`Payout run ${runPeriodLabel(detail.run)} finalised`);
      await Promise.all([reload(), loadDetail(detailId)]);
    } catch (err) {
      setDetailError((err as { message?: string }).message ?? 'Failed to finalise payout run');
    } finally {
      setFinalizing(false);
    }
  };

  const handleMarkPaid = async () => {
    if (!detailId || !detail) return;
    const ok = await confirm({
      title: 'Mark payout run paid?',
      message: `Record this payout run as paid? ${peopleCount(detail)} people, ${formatPeso(runTotalAmount(detail.run))}.`,
      confirmLabel: 'Mark paid',
    });
    if (!ok) return;
    setMarkingPaid(true);
    setDetailError('');
    try {
      await payoutsApi.markPaid(detailId);
      toast(`Payout run ${runPeriodLabel(detail.run)} marked paid`);
      await Promise.all([reload(), loadDetail(detailId)]);
    } catch (err) {
      setDetailError((err as { message?: string }).message ?? 'Failed to mark payout run paid');
    } finally {
      setMarkingPaid(false);
    }
  };

  const handleCreateCorrection = async (e: FormEvent) => {
    e.preventDefault();
    if (!correctionEntry) return;
    const current = currentEntryValue(correctionEntry, detail?.corrections);
    if (correctionAmount === '' || !Number.isFinite(Number(correctionAmount)) || Number(correctionAmount) < 0) {
      setCorrectionError('Enter the new amount (zero or more)');
      return;
    }
    if (Number(correctionAmount).toFixed(2) === Number(current).toFixed(2)) {
      setCorrectionError(`The new amount is the same as the current amount (${formatPeso(current)})`);
      return;
    }
    if (!correctionReason.trim()) {
      setCorrectionError('Reason is required');
      return;
    }
    setCorrectionSaving(true);
    setCorrectionError('');
    try {
      const res = await payoutsApi.createCorrection({
        originalEntryId: correctionEntry.id,
        amount: correctionAmount,
        reason: correctionReason.trim(),
      });
      toast(
        `Pay corrected from ${formatPeso(res.previousAmount)} to ${formatPeso(res.newAmount)}` +
          (res.mode === 'carry_forward'
            ? ` — the ${formatPeso(res.entryAmount)} difference goes into the next payout run`
            : ''),
      );
      setCorrectionEntry(null);
      setCorrectionAmount('');
      setCorrectionReason('');
      if (detailId) await loadDetail(detailId);
    } catch (err) {
      setCorrectionError((err as { message?: string }).message ?? 'Failed to create correction');
    } finally {
      setCorrectionSaving(false);
    }
  };

  const entryColumns = [
    { key: 'role', header: 'Role', value: (row: PayoutEntry) => row.role, render: (row: PayoutEntry) => humanize(row.role) },
    {
      key: 'personId',
      header: 'Person',
      value: (row: PayoutEntry) => row.personName ?? '',
      render: (row: PayoutEntry) => row.personName ?? '—',
    },
    {
      key: 'amount',
      header: 'Amount',
      value: (row: PayoutEntry) => Number(entryAmount(row)),
      render: (row: PayoutEntry) => (
        <span>
          <Money value={entryAmount(row)} />
          {row.entryKind && ENTRY_KIND_LABELS[row.entryKind] && (
            <span className="entry-kind-note">{ENTRY_KIND_LABELS[row.entryKind]}</span>
          )}
          {row.fromEarlierPeriod && (
            <span className="entry-kind-note">From earlier period · earned {formatDate(row.earnedAt ?? row.createdAt)}</span>
          )}
        </span>
      ),
    },
    { key: 'state', header: 'State', render: (row: PayoutEntry) => <StatusBadge status={row.state} size="sm" /> },
    {
      key: 'actions',
      header: '',
      sortable: false,
      render: (row: PayoutEntry) => (
        <Button
          type="button"
          size="sm"
          variant="secondary"
          onClick={() => {
            setCorrectionError('');
            setCorrectionAmount(currentEntryValue(row, detail?.corrections));
            setCorrectionReason('');
            setCorrectionEntry(row);
          }}
        >
          Correct
        </Button>
      ),
    },
  ];

  const handleAdd = async (e: FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      await payoutsApi.create({ period });
      toast(`Payout run created for ${periodChoices.find((o) => o.value === period)?.label ?? period}`);
      setShowAdd(false);
      setPeriod(currentFortnightPeriod());
      reload();
    } catch (err) {
      setError((err as { message?: string }).message ?? 'Failed to create payout run');
    } finally {
      setSaving(false);
    }
  };

  const columns = [
    {
      key: 'period',
      header: 'Period',
      render: (row: Payout) => runPeriodLabel(row),
    },
    {
      key: 'status',
      header: 'Status',
      value: (row: Payout) => row.status,
      render: (row: Payout) => <StatusBadge status={row.status} size="sm" />,
    },
    {
      key: 'totalAmount',
      header: 'Total',
      align: 'right' as const,
      value: (row: Payout) => Number(runTotalAmount(row)),
      render: (row: Payout) => <Money value={runTotalAmount(row)} />,
    },
    {
      key: 'createdAt',
      header: 'Created',
      value: (row: Payout) => row.createdAt,
      render: (row: Payout) => formatDate(row.createdAt),
    },
    {
      key: 'actions',
      header: '',
      sortable: false,
      render: (row: Payout) => (
        <Button type="button" size="sm" variant="secondary" onClick={() => loadDetail(row.id)}>
          View
        </Button>
      ),
    },
  ];

  return (
    <Layout navItems={adminNavItems}>
      <div className="page">
        <div className="page-header row-between">
          <div>
            <h1>Payouts</h1>
            <p className="text-muted">Crew compensation payouts (fortnightly)</p>
          </div>
          <Button type="button" onClick={() => { setError(''); setShowAdd(true); }}>New payout run</Button>
        </div>

        {listLoadError && (
          <p className="form-error" style={{ marginBottom: 'var(--space-md)' }}>
            Could not load payout runs: {listLoadError}{' '}
            <Button type="button" size="sm" variant="secondary" onClick={() => reload()}>Retry</Button>
          </p>
        )}

        <DataTable
          columns={columns}
          data={payouts ?? []}
          keyField="id"
          loading={loading}
          dense
          searchPlaceholder="Filter payouts…"
          emptyMessage={
            listLoadError
              ? 'Payout runs could not be loaded.'
              : 'No payout runs yet. Create one for a fortnight after trips are completed and pay has accrued.'
          }
        />

        <Modal open={showAdd} onClose={() => setShowAdd(false)} title="New payout run">
          <form onSubmit={handleAdd}>
            {error && <div className="form-error" style={{ marginBottom: 'var(--space-md)' }}>{error}</div>}
            <Select
              label="Fortnight period"
              value={period}
              onChange={(e) => setPeriod(e.target.value)}
              required
            >
              {periodChoices.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </Select>
            <p className="text-muted" style={{ fontSize: '0.8125rem', marginBottom: 'var(--space-md)' }}>
              Payouts run fortnightly: 1–15 and 16–month end. The run collects all pay earned in the
              selected fortnight, plus any earlier pay that wasn&apos;t in a payout run yet.
            </p>
            <Button type="submit" loading={saving}>Create payout</Button>
          </form>
        </Modal>

        <Modal
          open={detailId !== null}
          onClose={closeDetail}
          title={detail ? `Payout ${runPeriodLabel(detail.run)}` : 'Payout run detail'}
          size="lg"
        >
          {detailLoading && !detail && <p className="text-muted">Loading…</p>}
          {detailError && <p className="form-error" style={{ marginBottom: 'var(--space-md)' }}>{detailError}</p>}
          {detail && (
            <div>
              <div className="grid-3" style={{ marginBottom: 'var(--space-lg)' }}>
                <div className="stat-block">
                  <span className="stat-value"><StatusBadge status={detail.run.status} size="sm" /></span>
                  <span className="stat-label">Status</span>
                </div>
                <div className="stat-block">
                  <span className="stat-value">{formatPeso(runTotalAmount(detail.run))}</span>
                  <span className="stat-label">Total</span>
                </div>
                <div className="stat-block">
                  <span className="stat-value">{detail.entries.length}</span>
                  <span className="stat-label">Entries</span>
                </div>
              </div>

              <div className="row-between" style={{ marginBottom: 'var(--space-sm)' }}>
                <h3>Entries</h3>
                <div className="row" style={{ gap: '0.5rem' }}>
                  {canFinalizeRun(detail.run) && (
                    <Button type="button" size="sm" loading={finalizing} onClick={handleFinalize}>
                      Finalise
                    </Button>
                  )}
                  {!detail.run.finalizedAt && !canFinalizeRun(detail.run) && (
                    <p className="text-muted" style={{ fontSize: '0.8125rem', margin: 0 }}>
                      Older monthly run — it can&apos;t be finalised. Create a new fortnightly run instead.
                    </p>
                  )}
                  {detail.run.finalizedAt && !detail.run.paidAt && (
                    <Button type="button" size="sm" loading={markingPaid} onClick={handleMarkPaid}>
                      Mark paid
                    </Button>
                  )}
                </div>
              </div>

              <DataTable
                columns={entryColumns}
                data={detail.entries}
                keyField="id"
                dense
                emptyMessage="No pay entries in this run."
              />

              <h3 style={{ margin: 'var(--space-lg) 0 var(--space-sm)' }}>Per-person statements</h3>
              {detail.statements.length ? (
                <div>
                  {detail.statements.map((stmt) => (
                    <div key={stmt.id} className="frozen-line-row">
                      <span className="row" style={{ gap: '0.5rem' }}>
                        {stmt.personName ?? 'Unnamed person'} <StatusBadge status={stmt.status} size="sm" />
                      </span>
                      <span className="money-cell">{formatPeso(stmt.netTotal)}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-muted">Finalise the run to generate immutable per-person statements.</p>
              )}

              {detail.corrections && detail.corrections.length > 0 && (
                <>
                  <h3 style={{ margin: 'var(--space-lg) 0 var(--space-sm)' }}>Correction history</h3>
                  <CorrectionHistory items={detail.corrections} />
                </>
              )}
            </div>
          )}
        </Modal>

        <Modal
          open={correctionEntry !== null}
          onClose={() => setCorrectionEntry(null)}
          title="Correct pay entry"
          size="sm"
        >
          <form onSubmit={handleCreateCorrection}>
            {correctionError && (
              <div className="form-error" style={{ marginBottom: 'var(--space-md)' }}>{correctionError}</div>
            )}
            {correctionEntry && (
              <p className="text-muted" style={{ fontSize: '0.8125rem', marginBottom: 'var(--space-md)' }}>
                {correctionEntry.personName ?? 'This person'} · {humanize(correctionEntry.role)}.{' '}
                {detail?.run.finalizedAt
                  ? 'This payout run is already finalised, so it stays as issued. The difference between the current and new amount is added to the next payout run.'
                  : 'The new amount replaces the current one. The change, reason and who made it are kept in the correction history.'}
              </p>
            )}
            <Input
              label="New amount (₱)"
              type="number"
              inputMode="decimal"
              step="0.01"
              min="0"
              value={correctionAmount}
              onChange={(e) => setCorrectionAmount(e.target.value)}
              hint={
                correctionEntry
                  ? `Replaces ${formatPeso(currentEntryValue(correctionEntry, detail?.corrections))}`
                  : undefined
              }
              required
            />
            <div className="input-group">
              <label className="input-label" htmlFor="correction-reason">
                Reason<span className="input-required" aria-hidden="true"> *</span>
              </label>
              <textarea
                id="correction-reason"
                className="input-field"
                rows={3}
                value={correctionReason}
                onChange={(e) => setCorrectionReason(e.target.value)}
                required
              />
            </div>
            <Button type="submit" loading={correctionSaving}>Save correction</Button>
          </form>
        </Modal>
      </div>
      <style>{`
        .money-cell {
          display: block;
          text-align: right;
          font-variant-numeric: tabular-nums;
          white-space: nowrap;
        }
        .entry-kind-note {
          display: block;
          text-align: right;
          font-size: 0.75rem;
          color: var(--color-text-muted);
        }
        .correction-history {
          list-style: none;
          padding: 0;
          margin: 0;
        }
        .correction-history li {
          padding: var(--space-sm) 0;
          border-bottom: 1px solid var(--color-border);
          font-size: 0.875rem;
        }
        .correction-meta {
          font-size: 0.8125rem;
          margin-top: 2px;
        }
        .frozen-line-row {
          display: flex;
          justify-content: space-between;
          padding: var(--space-sm) 0;
          border-bottom: 1px solid var(--color-border);
          font-size: 0.875rem;
        }
        .form-error {
          background: #FDE8E8;
          color: var(--color-urgent);
          padding: var(--space-sm) var(--space-md);
          border-radius: var(--radius-sm);
          font-size: 0.875rem;
        }
      `}</style>
    </Layout>
  );
}
